import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import {
  addMenuCategory,
  deleteMenuCategory,
  getMenuCategories,
  updateMenuCategory,
} from "Api/apiService";

const CATEGORY_TABS = [
  { key: "menu", label: "Menu" },
  { key: "drink", label: "Drink Menu" },
  { key: "happyHour", label: "Happy Hour Menu" },
  { key: "brunch", label: "Brunch Menu" },
];

const normalizeValue = (value) => String(value || "").trim();

export default function MenuCategories() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [categoriesByType, setCategoriesByType] = useState({});
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const textColor = useColorModeValue("navy.700", "white");
  const labelColor = useColorModeValue("secondaryGray.600", "whiteAlpha.700");

  const activeType = CATEGORY_TABS[activeIndex]?.key || "menu";

  const loadCategories = useCallback(
    async (type) => {
      setLoading(true);
      try {
        const response = await getMenuCategories(type);
        if (Array.isArray(response.data.categories)) {
          setCategoriesByType((prev) => ({
            ...prev,
            [type]: response.data.categories,
          }));
        }
      } catch (error) {
        console.error("Failed to load categories", error);
        toast({
          title: "Unable to load categories.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    loadCategories(activeType);
    setNewCategoryName("");
    cancelEditing();
  }, [activeType, loadCategories]);

  const handleAddCategory = async (type) => {
    const trimmedName = normalizeValue(newCategoryName);
    if (!trimmedName) {
      toast({
        title: "Enter a category name.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    try {
      await addMenuCategory({ type, name: trimmedName });
      setNewCategoryName("");
      await loadCategories(type);
      toast({
        title: "Category added.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to add category", error);
      toast({
        title: "Unable to add category.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const startEditing = (category) => {
    setEditingId(category._id);
    setEditingName(category.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleSaveEdit = async (categoryId, type) => {
    const trimmedName = normalizeValue(editingName);
    if (!trimmedName) {
      toast({
        title: "Enter a category name.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    try {
      await updateMenuCategory({ categoryId, name: trimmedName });
      cancelEditing();
      await loadCategories(type);
      toast({
        title: "Category updated.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to update category", error);
      toast({
        title: "Unable to update category.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (categoryId, type) => {
    try {
      await deleteMenuCategory(categoryId);
      await loadCategories(type);
      toast({
        title: "Category deleted.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to delete category", error);
      toast({
        title: "Unable to delete category.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid columns={{ base: 1, xl: 1 }} spacing="20px">
        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Menu Categories
            </Text>
            <Text fontSize="14px" color={labelColor}>
              Manage categories for Menu, Drink Menu, Happy Hour, and Brunch
              items.
            </Text>
            <Divider />
            <Tabs
              index={activeIndex}
              onChange={(index) => {
                setActiveIndex(index);
                cancelEditing();
              }}
              variant="enclosed"
            >
              <TabList>
                {CATEGORY_TABS.map((tab) => (
                  <Tab key={tab.key}>{tab.label}</Tab>
                ))}
              </TabList>
              <TabPanels>
                {CATEGORY_TABS.map((tab) => (
                  <TabPanel key={tab.key} px={0}>
                    <Stack spacing="14px">
                      <FormControl>
                        <FormLabel color={labelColor}>Add Category</FormLabel>
                        <Flex gap="10px">
                          <Input
                            value={newCategoryName}
                            onChange={(event) =>
                              setNewCategoryName(event.target.value)
                            }
                            placeholder={`New ${tab.label} category`}
                          />
                          <Button
                            colorScheme="blue"
                            onClick={() => handleAddCategory(tab.key)}
                            isLoading={loading}
                          >
                            Add
                          </Button>
                        </Flex>
                      </FormControl>
                      <Divider />
                      {(categoriesByType[tab.key] || []).length === 0 ? (
                        <Text color={labelColor}>
                          No categories yet. Add one above.
                        </Text>
                      ) : (
                        <Stack spacing="10px">
                          {(categoriesByType[tab.key] || []).map((category) => (
                            <Flex
                              key={category._id}
                              align="center"
                              justify="space-between"
                              borderWidth="1px"
                              borderColor="gray.200"
                              borderRadius="12px"
                              p="12px"
                              gap="12px"
                            >
                              {editingId === category._id ? (
                                <Input
                                  value={editingName}
                                  onChange={(event) =>
                                    setEditingName(event.target.value)
                                  }
                                />
                              ) : (
                                <Text fontWeight="600" color={textColor}>
                                  {category.name}
                                </Text>
                              )}
                              <Flex gap="8px">
                                {editingId === category._id ? (
                                  <>
                                    <Button
                                      size="sm"
                                      colorScheme="blue"
                                      onClick={() =>
                                        handleSaveEdit(category._id, tab.key)
                                      }
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={cancelEditing}
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => startEditing(category)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      colorScheme="red"
                                      variant="outline"
                                      onClick={() =>
                                        handleDelete(category._id, tab.key)
                                      }
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </Flex>
                            </Flex>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Flex>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
